/**
 * Structured application logger.
 *
 * PHI RULE: the `data` parameter accepts only primitive values
 * (string | number | boolean | null | undefined).  Never pass free-text
 * clinical content — transcripts, narratives, notes, patient names — as a
 * value.  Patient / session / visit IDs are fine.
 *
 * In development: writes to the browser console.
 * In production:  warn/error levels are forwarded to Sentry as
 *                 captureMessage / captureException events so they are
 *                 visible without a user needing to open DevTools.
 * Info level is local-only to keep Sentry volume low.
 */

import * as Sentry from "@sentry/react";

export type SafeValue = string | number | boolean | null | undefined;
export type SafeData = Record<string, SafeValue>;

const isDev = import.meta.env.DEV;

function toExtra(data?: SafeData): Record<string, SafeValue> | undefined {
  return data && Object.keys(data).length > 0 ? data : undefined;
}

export const logger = {
  info(message: string, data?: SafeData): void {
    // eslint-disable-next-line no-console
    if (isDev) console.info(`[INFO] ${message}`, data ?? "");
  },

  warn(message: string, data?: SafeData): void {
    if (isDev) console.warn(`[WARN] ${message}`, data ?? "");
    Sentry.captureMessage(message, {
      level: "warning",
      extra: toExtra(data),
    });
  },

  /**
   * Log an error-level event.
   *
   * @param message  Short, static description (no PHI).
   * @param error    Optional Error instance OR structured data if no Error is
   *                 available.  When an Error is passed, it is sent as an
   *                 exception so Sentry can group by stack trace.
   * @param data     Additional structured context (used when `error` is an
   *                 Error instance and you also want extra fields).
   */
  error(message: string, error?: Error | SafeData, data?: SafeData): void {
    if (isDev) console.error(`[ERROR] ${message}`, error ?? "", data ?? "");

    if (error instanceof Error) {
      Sentry.captureException(error, {
        extra: { log_message: message, ...toExtra(data) },
      });
    } else {
      Sentry.captureMessage(message, {
        level: "error",
        extra: toExtra({ ...(error ?? {}), ...(data ?? {}) } as SafeData),
      });
    }
  },
};
